import React from 'react';

import { scorePassword } from '../../../../utils/password-strength';
import { validateMailboxPassword } from '../../state';
import { FieldWrap, FieldError } from '../FieldRow/FieldRow.styles';
import { PasswordField } from '../PasswordField';
import { FieldHint, MeterSlot, Meter, Bars, Bar, MeterCaption } from './PasswordEntry.styles';

export type PasswordEntryProps = {
  /** The field's label (translated). */
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Replace the value with a fresh generated password. */
  onRegenerate: () => void;
  /** The copy button's accessible name (translated). */
  copyLabel: string;
  /** The operator typed this value (their own): the strength meter shows. A generated value is not
   *  an edit, and always scores strong, so no meter for it. */
  edited: boolean;
  disabled?: boolean;
  /** The provider's refusal of this password, verbatim, in the message line. */
  refusal?: string | null;
  /** Show the "Shown once" hint when nothing else holds the message line. */
  hint?: boolean;
};

/**
 * A mailbox password as the operator sets it, wherever they do (the create form, the rotate dialog):
 * the password row, then ONE message line directly under it, then the strength meter. Validation of
 * the service's bounds (8 to 54, no edge whitespace) is read from `validateMailboxPassword` here, so
 * the two places cannot word it differently; the caller gates its own submit on the same function.
 */
export const PasswordEntry = ({
  label,
  value,
  onChange,
  onRegenerate,
  copyLabel,
  edited,
  disabled = false,
  refusal = null,
  hint = false,
}: PasswordEntryProps): React.ReactElement => {
  const passwordError = validateMailboxPassword(value);
  const strength = React.useMemo(() => scorePassword(value), [value]);
  return (
    <FieldWrap>
      <PasswordField
        label={label}
        value={value}
        onChange={onChange}
        onRegenerate={onRegenerate}
        copyLabel={copyLabel}
        disabled={disabled}
      />
      {/* One line directly under the field, most specific first: the local bounds, then the
          provider's own refusal (verbatim), then the hint. The extractor only sees literal
          `text`, so each local rule is its own branch. Too short is the meter's to say once it
          shows. */}
      {passwordError === 'short' && edited ? null : passwordError === 'short' ? (
        <FieldError text="Use at least 8 characters." tag="span" />
      ) : passwordError === 'long' ? (
        <FieldError text="Use at most 54 characters." tag="span" />
      ) : passwordError === 'edge-whitespace' ? (
        <FieldError text="Remove the spaces at the start or end. Spaces inside are fine." tag="span" />
      ) : refusal ? (
        <FieldError text={refusal} tag="span" />
      ) : hint ? (
        <FieldHint text="Shown once. Save it now: you need it to sign in to webmail or a mail client." tag="span" />
      ) : null}
      {/* Under the message line: the slot is always there (no reserved height, so an empty slot
          takes no room) and gives the live region a stable home; the meter only once the value
          is theirs. Bars are decorative (aria-hidden); the caption carries the meaning,
          announced as it changes. `data-level` is a deliberate test hook for the four states. */}
      <MeterSlot aria-live="polite">
        {edited ? (
          <Meter data-level={strength.level}>
            <Bars aria-hidden="true">
              <Bar $filled={strength.score >= 1} $level={strength.level} />
              <Bar $filled={strength.score >= 2} $level={strength.level} />
              <Bar $filled={strength.score >= 3} $level={strength.level} />
            </Bars>
            {/* Four static literals, the same keys `/register` already translates. */}
            {strength.level === 'too-short' ? (
              <MeterCaption text="Use at least 8 characters." tag="span" $level={strength.level} />
            ) : strength.level === 'weak' ? (
              <MeterCaption text="Weak password" tag="span" $level={strength.level} />
            ) : strength.level === 'fair' ? (
              <MeterCaption text="Fair password" tag="span" $level={strength.level} />
            ) : (
              <MeterCaption text="Strong password" tag="span" $level={strength.level} />
            )}
          </Meter>
        ) : null}
      </MeterSlot>
    </FieldWrap>
  );
};

export default PasswordEntry;
